import { useCallback, useEffect, useRef, useState } from 'react';
import { TIMER_STORAGE_KEY } from '../data/brainTeasers';

function pad2(n) {
  return n > 9 ? String(n) : `0${n}`;
}

function formatTime(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
}

function elapsedFromStoredStart() {
  const raw = localStorage.getItem(TIMER_STORAGE_KEY);
  if (!raw) return 0;
  const start = new Date(raw).getTime();
  if (Number.isNaN(start)) return 0;
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}

/**
 * PHP-faithful count-up timer.
 * Round 1 always resets cookie/start; later rounds resume from bt_gettime.
 */
export default function useGameTimer(resetOnMount) {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (resetOnMount) {
      const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
      localStorage.setItem(TIMER_STORAGE_KEY, now);
      setElapsed(0);
    } else {
      if (!localStorage.getItem(TIMER_STORAGE_KEY)) {
        const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
        localStorage.setItem(TIMER_STORAGE_KEY, now);
      }
      setElapsed(elapsedFromStoredStart());
    }

    intervalRef.current = setInterval(() => {
      setElapsed((prev) => {
        if (resetOnMount) return prev + 1;
        return elapsedFromStoredStart();
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [resetOnMount]);

  const timeString = formatTime(elapsed);

  const getTimeString = useCallback(() => formatTime(elapsed), [elapsed]);

  return { elapsed, timeString, getTimeString };
}
